import React, { useState, useMemo, useEffect } from "react";
import PieChartComponent from "./PieChart";
import { Gender, GenderLabels } from "../../constants";
import { AgeRange } from "./useAgeRanges";

// Εισαγωγή των hooks
import { useAllergies } from "../../hooks/useAllergies";
import { usePatientAllergies } from "../../hooks/usePatientAllergies";
import { useSurgeryTypes } from "../../hooks/useSurgeryTypes";
import { usePatientSurgeries } from "../../hooks/usePatientSurgeries";
import { useMedicines } from "../../hooks/useMedicines";
import { usePatientMedicines } from "../../hooks/usePatientMedicines";
import { useRehabProtocols } from "../../hooks/useRehabProtocols";
import { usePatientRehabProtocols } from "../../hooks/usePatientRehabProtocols";
import { useImplants } from "../../hooks/useImplants";
import { usePatientImplants } from "../../hooks/usePatientImplants";

interface CategoryStatisticsContainerProps {
  patients: any[];
  activeFilters: Record<string, any>;
  loadingPatients: boolean;
  onFilteredPatientsChange: (filtered: any[]) => void;
}

export const Categories: React.FC<CategoryStatisticsContainerProps> = ({
  patients,
  activeFilters,
  loadingPatients,
  onFilteredPatientsChange,
}) => {
  const [selectedSlice, setSelectedSlice] = useState<string | null>(null);

  // 1. Εντοπισμός ενεργών φίλτρων (Ασφαλές για null/undefined)
  const activeCategories = useMemo(() => {
    const filters = activeFilters || {};
    const keys = Object.keys(filters).filter(
      (k) => filters[k] === true && k !== "ageRanges",
    );
    return keys.length > 0 ? keys : ["gender"];
  }, [activeFilters]);

  // Reset της επιλογής slice όταν αλλάζουν τα ενεργά φίλτρα στο Sidebar
  useEffect(() => {
    setSelectedSlice(null);
  }, [activeCategories]);

  // 2. Κλήση των Hooks
  const { allergies: rawAllergies, isLoading: loadingAllergies } =
    useAllergies();
  const { patientAllergies: rawPA, isLoading: loadingPA } =
    usePatientAllergies();

  const { surgeryTypes: rawSurgeries, isLoading: loadingSurgeries } =
    useSurgeryTypes();
  const { patientSurgeries: rawPS, isLoading: loadingPS } =
    usePatientSurgeries();

  const { medicines: rawMedications, isLoading: loadingMedications } =
    useMedicines();
  const { patientMedicines: rawPM, isLoading: loadingPM } =
    usePatientMedicines();

  const { rehabProtocols: rawRP, isLoading: loadingRP } = useRehabProtocols();
  const { patientRehabProtocols: rawPRP, isLoading: loadingPRP } =
    usePatientRehabProtocols();

  const { implants: rawImplants, isLoading: loadingImplants } = useImplants();
  const { patientImplants: rawPI, isLoading: loadingPI } = usePatientImplants();

  // Συνολικό state φόρτωσης (χρησιμοποιεί όλες τις loading μεταβλητές, διορθώνοντας το "unused vars")
  const isLoading =
    loadingPatients ||
    loadingAllergies ||
    loadingPA ||
    loadingSurgeries ||
    loadingPS ||
    loadingMedications ||
    loadingPM ||
    loadingRP ||
    loadingPRP ||
    loadingImplants ||
    loadingPI;

  // 3. Δημιουργία Lookup Maps
  const lookups = useMemo(() => {
    if (isLoading) {
      return {
        allergyMap: new Map(),
        surgeryMap: new Map(),
        medMap: new Map(),
        rehabMap: new Map(),
        implantMap: new Map(),
      };
    }

    const allergyMap = new Map<any, string>();
    (rawAllergies || []).forEach((a: any) => {
      if (a)
        allergyMap.set(
          a.id ?? a.allergyId ?? a.allergyID,
          a.allergyDescr || a.description || a.name || a.title,
        );
    });

    const surgeryMap = new Map<any, string>();
    (rawSurgeries || []).forEach((s: any) => {
      if (s)
        surgeryMap.set(
          s.id ?? s.surgeryId ?? s.surgeryID,
          s.surgeryDescr || s.description || s.name || s.title,
        );
    });

    const medMap = new Map<any, string>();
    (rawMedications || []).forEach((m: any) => {
      if (m)
        medMap.set(
          m.id ?? m.medicationId ?? m.medicationID,
          m.medicationDescr || m.description || m.name || m.title,
        );
    });

    const rehabMap = new Map<any, string>();
    (rawRP || []).forEach((r: any) => {
      if (r)
        rehabMap.set(
          r.id ?? r.rehabProtocolId ?? r.rehabProtocolID,
          r.rehabDescr || r.description || r.name || r.title,
        );
    });

    const implantMap = new Map<any, string>();
    (rawImplants || []).forEach((imp: any) => {
      if (imp)
        implantMap.set(
          imp.id ?? imp.implantId ?? imp.implantID,
          imp.implantDescr || imp.description || imp.name || imp.title,
        );
    });

    return { allergyMap, surgeryMap, medMap, rehabMap, implantMap };
  }, [
    isLoading,
    rawAllergies,
    rawSurgeries,
    rawMedications,
    rawRP,
    rawImplants,
  ]);

  // 4. Υπολογισμός Δεδομένων & Map Ασθενών
  const { chartData, groupedPatientsMap } = useMemo(() => {
    const safePatients = patients || [];

    if (safePatients.length === 0 || isLoading) {
      return { chartData: [], groupedPatientsMap: new Map<string, any[]>() };
    }

    const patientMap = new Map<string, any[]>();
    const counts: Record<string, number> = {};
    const currentYear = new Date().getFullYear();

    // Χρήση fallback εσωτερικά στο useMemo
    const paData = rawPA || [];
    const psData = rawPS || [];
    const pmData = rawPM || [];
    const prpData = rawPRP || [];
    const piData = rawPI || [];

    safePatients.forEach((patient) => {
      if (!patient) return;

      const pId = patient.id || patient.patientId;
      const patientLabelsMap: Record<string, string[]> = {};

      activeCategories.forEach((cat) => {
        if (cat === "gender") {
          const g = patient.gender ?? Gender.NONE;
          patientLabelsMap["gender"] = [
            GenderLabels[g as keyof typeof GenderLabels] || "Άγνωστο",
          ];
        } else if (cat === "age") {
          const ranges: AgeRange[] =
            activeFilters?.ageRanges && activeFilters.ageRanges.length > 0
              ? activeFilters.ageRanges
              : [
                  { id: "1", from: 0, to: 24 },
                  { id: "2", from: 25, to: 49 },
                  { id: "3", from: 50, to: 74 },
                  { id: "4", from: 75, to: 100 },
                ];
          const rawBirthDate =
            patient.birthDate ||
            patient.birthdate ||
            patient.dateOfBirth ||
            patient.birthYear;
          let ageLabel = "Άγνωστη Ηλικία";

          if (rawBirthDate) {
            const birthYear = new Date(rawBirthDate).getFullYear();
            if (!isNaN(birthYear)) {
              const age = currentYear - birthYear;
              const matchedRange = ranges.find(
                (r) => age >= r.from && age <= r.to,
              );
              if (matchedRange)
                ageLabel = `${matchedRange.from}-${matchedRange.to} ετών`;
            }
          }
          patientLabelsMap["age"] = [ageLabel];
        } else if (cat === "allergies") {
          const myItems = paData.filter(
            (pa: any) => pa && (pa.patientId || pa.patientID) === pId,
          );
          const labels =
            myItems.length > 0
              ? myItems.map(
                  (pa: any) =>
                    lookups.allergyMap.get(
                      pa.allergyId ?? pa.allergyID ?? pa.id,
                    ) ||
                    pa.allergyDescr ||
                    pa.description ||
                    pa.name ||
                    "Άγνωστη Αλλεργία",
                )
              : ["Χωρίς Αλλεργίες"];
          patientLabelsMap["allergies"] = Array.from(new Set(labels));
        } else if (cat === "surgery") {
          const myItems = psData.filter(
            (ps: any) => ps && (ps.patientId || ps.patientID) === pId,
          );
          const labels =
            myItems.length > 0
              ? myItems.map(
                  (ps: any) =>
                    lookups.surgeryMap.get(
                      ps.surgeryId ?? ps.surgeryID ?? ps.id,
                    ) ||
                    ps.surgeryDescr ||
                    ps.description ||
                    ps.name ||
                    "Άγνωστο Χειρουργείο",
                )
              : ["Χωρίς Χειρουργείο"];
          patientLabelsMap["surgery"] = Array.from(new Set(labels));
        } else if (cat === "medication") {
          const myItems = pmData.filter(
            (pm: any) => pm && (pm.patientId || pm.patientID) === pId,
          );
          const labels =
            myItems.length > 0
              ? myItems.map(
                  (pm: any) =>
                    lookups.medMap.get(
                      pm.medicationId ?? pm.medicationID ?? pm.id,
                    ) ||
                    pm.medicationDescr ||
                    pm.description ||
                    pm.name ||
                    "Άγνωστο Φάρμακο",
                )
              : ["Χωρίς Φάρμακα"];
          patientLabelsMap["medication"] = Array.from(new Set(labels));
        } else if (cat === "rehab") {
          const myItems = prpData.filter(
            (prp: any) => prp && (prp.patientId || prp.patientID) === pId,
          );
          const labels =
            myItems.length > 0
              ? myItems.map(
                  (prp: any) =>
                    lookups.rehabMap.get(
                      prp.rehabProtocolId ?? prp.rehabProtocolID ?? prp.id,
                    ) ||
                    prp.rehabDescr ||
                    prp.description ||
                    prp.name ||
                    "Άγνωστη Αποκατάσταση",
                )
              : ["Χωρίς Αποκατάσταση"];
          patientLabelsMap["rehab"] = Array.from(new Set(labels));
        } else if (cat === "implants") {
          const myItems = piData.filter(
            (pi: any) => pi && (pi.patientId || pi.patientID) === pId,
          );
          const labels =
            myItems.length > 0
              ? myItems.map(
                  (pi: any) =>
                    lookups.implantMap.get(
                      pi.implantId ?? pi.implantID ?? pi.id,
                    ) ||
                    pi.implantDescr ||
                    pi.description ||
                    pi.name ||
                    "Άγνωστο Εμφύτευμα",
                )
              : ["Χωρίς Εμφυτεύματα"];
          patientLabelsMap["implants"] = Array.from(new Set(labels));
        }
      });

      const generateCombinations = (keys: string[], index = 0): string[][] => {
        if (index === keys.length) return [[]];
        const currentKey = keys[index];
        const values = patientLabelsMap[currentKey] || [];
        const res: string[][] = [];
        const subCombos = generateCombinations(keys, index + 1);

        values.forEach((v) => {
          subCombos.forEach((sub) => {
            res.push([v, ...sub]);
          });
        });
        return res;
      };

      const rawCombinations = generateCombinations(activeCategories);
      const uniqueCombinations = Array.from(
        new Set(rawCombinations.map((combo) => combo.join(" | "))),
      );

      uniqueCombinations.forEach((compositeLabel) => {
        counts[compositeLabel] = (counts[compositeLabel] || 0) + 1;

        if (!patientMap.has(compositeLabel)) {
          patientMap.set(compositeLabel, []);
        }

        const currentList = patientMap.get(compositeLabel)!;
        if (!currentList.some((p) => (p.id || p.patientId) === pId)) {
          currentList.push(patient);
        }
      });
    });

    const formattedChartData = Object.keys(counts).map((key) => ({
      key: key,
      label: key,
      count: counts[key],
    }));

    return { chartData: formattedChartData, groupedPatientsMap: patientMap };
  }, [
    patients,
    activeCategories,
    activeFilters,
    isLoading,
    lookups,
    rawPA,
    rawPS,
    rawPM,
    rawPRP,
    rawPI,
  ]);

  // 5. ΧΕΙΡΙΣΜΟΣ ΚΛΙΚ ΣΤΟ PIECHART
  const handleSliceClick = (rawKey: any) => {
    const keyStr = rawKey !== null ? String(rawKey) : null;

    if (keyStr === null || selectedSlice === keyStr) {
      setSelectedSlice(null);
      onFilteredPatientsChange(patients || []);
    } else {
      setSelectedSlice(keyStr);
      const filtered = groupedPatientsMap.get(keyStr) || [];
      onFilteredPatientsChange(filtered);
    }
  };

  return (
    <PieChartComponent
      data={chartData}
      onSliceClick={handleSliceClick}
      selectedSlice={selectedSlice}
      loading={isLoading}
    />
  );
};

export default Categories;
